import React from "react"
import {
  Box, Center, HStack, Text, useToken,
} from "@chakra-ui/react"
import {
  CategoryScale, Chart as ChartJS, Filler, LinearScale, LineController, LineElement,
  PointElement, Tooltip,
} from "chart.js"
import { Chart } from "react-chartjs-2"
import Spinner from "components/elements/Spinner"
import { useEventPageViews } from "queries/events"
import { CHART_COLORS } from "utilities/constants"
import {
  defaultRange, fillDays, periodLabel, periodsBetween,
} from "../activitySeries"

ChartJS.register(
  CategoryScale, Filler, LinearScale, LineController, LineElement, PointElement, Tooltip,
)

type Props = {
  eventId: string
  timezone: string
  withLegend?: boolean
}

const LINES = [
  { key: "webPage", label: "Web Page", color: CHART_COLORS[0] },
  { key: "portalAndForms", label: "Portal & Forms", color: "purple.400" },
] as const

const PageViewsChart = ({ eventId, timezone, withLegend = false }: Props) => {
  const range = defaultRange("daily", new Date(), timezone)
  const { data, isLoading, isError } = useEventPageViews(eventId, range)
  const [gridColor, textColor, ...lineColors] = useToken(
    "colors",
    ["gray.200", "gray.600", ...LINES.map(({ color }) => color)],
  )

  const message = (text: string) => <Center h="150px"><Text color="gray.600">{text}</Text></Center>

  if (isError) return message("The chart couldn't be loaded.")
  if (isLoading || !data) return <Center h="150px"><Spinner /></Center>

  // Portal and form views only count once the public site reports them; until then
  // that line stays out.
  const lines = LINES
    .map((line, index) => ({ ...line, color: lineColors[index], views: data[line.key] ?? [] }))
    .filter(({ key, views }) => key === "webPage" || views.length)
  if (lines.every(({ views }) => !views.length)) {
    return message("No views counted in the last 30 days.")
  }

  const periods = periodsBetween("daily", range.from!, range.to)

  const chart = (
    <Box h="150px" data-testid="page-views-chart">
      <Chart
        type="line"
        data={{
          labels: periods.map((period) => periodLabel("daily", period)),
          datasets: lines.map(({ label, color, views }, index) => ({
            label,
            data: fillDays(periods, Object.fromEntries(views.map(({ date, views: count }) => [
              date, count,
            ]))),
            borderColor: color,
            backgroundColor: `${color}22`,
            fill: index === 0,
            cubicInterpolationMode: "monotone" as const,
            pointRadius: 0,
            pointHoverRadius: 4,
          })),
        }}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: textColor, maxTicksLimit: 4, maxRotation: 0 },
            },
            y: {
              beginAtZero: true,
              suggestedMax: 10,
              grid: { color: gridColor },
              ticks: { color: textColor, precision: 0, maxTicksLimit: 3 },
            },
          },
        }}
      />
    </Box>
  )

  if (!withLegend || lines.length < 2) return chart

  return (
    <>
      <HStack spacing={4} mb={2} fontSize="xs" color="gray.600">
        {lines.map(({ label, color }) => (
          <HStack key={label} spacing={1.5}>
            <Box boxSize={2} borderRadius="full" bg={color} />
            <Text>{label}</Text>
          </HStack>
        ))}
      </HStack>
      {chart}
    </>
  )
}

export default PageViewsChart
